import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Eye, EyeOff, Check, X, Loader2, Mail, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AppleSignInButton from '../components/AppleSignInButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [step, setStep] = useState(1); // 1: Email, 2: Verify Code, 3: Complete Registration
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    platform: 'cloudgm',
    role: 'customer'
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailVerified, setEmailVerified] = useState(false);
  const navigate = useNavigate();

  // Password validation rules
  const passwordRules = {
    minLength: formData.password.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  const validateEmail = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors({ email: t('auth.invalidEmail') });
      return false;
    }
    setErrors({});
    return true;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!/^[A-Za-z]+$/.test(formData.firstName)) {
      newErrors.firstName = t('auth.englishOnly');
    }

    if (!/^[A-Za-z]+$/.test(formData.lastName)) {
      newErrors.lastName = t('auth.englishOnly');
    }

    if (!isPasswordValid) {
      newErrors.password = t('auth.passwordRequirements');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.confirmPassword');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: Send verification code
  const handleSendCode = async (e) => {
    e.preventDefault();
    
    if (!validateEmail()) {
      toast.error(t('auth.invalidEmail'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/send-email-verification?email=${encodeURIComponent(formData.email)}&platform=${formData.platform}`);
      toast.success(t('auth.codeSentTo') + ' ' + formData.email);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error(t('auth.enterCode'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/verify-email-code?email=${encodeURIComponent(formData.email)}&code=${verificationCode}`);
      toast.success(t('auth.emailVerified') + ' ✓');
      setEmailVerified(true);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Complete registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('common.error'));
      return;
    }

    if (!emailVerified) {
      toast.error(t('auth.emailVerified'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
        name: `${formData.firstName} ${formData.lastName}`,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        platform: formData.platform,
        role: formData.role
      });
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success(t('auth.register') + ' ✓');
      navigate('/');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const PasswordRule = ({ passed, text, isDarkMode }) => (
    <div className={`flex items-center gap-2 text-sm ${
      passed 
        ? 'text-green-500' 
        : isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'
    }`}>
      {passed ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      <span>{text}</span>
    </div>
  );

  // Progress Steps
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((s, idx) => (
        <div key={s} className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
            step >= s ? 'bg-[#1E9BD9] text-white' : isDarkMode ? 'bg-white/10 text-gray-500' : 'bg-[#E2E8F0] text-[#94A3B8]'
          }`}>
            {step > s ? <Check className="h-5 w-5" /> : s}
          </div>
          {idx < 2 && (
            <div className={`w-16 h-1 mx-2 transition-all ${step > s ? 'bg-[#1E9BD9]' : isDarkMode ? 'bg-white/10' : 'bg-[#E2E8F0]'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#f8fafc]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-20 start-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'} rounded-full blur-[100px]`} />
      </div>

      <div className="relative max-w-lg w-full">
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/pggsc8pu_freepik__background__28286.png"
            alt="CloudGM Logo"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} data-testid="register-title">
            {t('auth.register')}
          </h1>
          <p className={isDarkMode ? 'text-gray-400' : 'text-[#475569]'}>
            {step === 1 && t('auth.enterEmailToStart')}
            {step === 2 && t('auth.enterCodeSent')}
            {step === 3 && t('auth.completeAccount')}
          </p>
        </div>

        <StepIndicator />

        <div className={`rounded-3xl p-8 ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white shadow-[0_20px_60px_-15px_rgba(30,155,217,0.15)] border border-[#E2E8F0]'}`}>
          
          {/* Step 1: Email Entry */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Social sign-up — matched full-width, h-12 rounded-xl */}
              <div className="flex flex-col gap-2.5">
                <div data-testid="google-register-wrapper" className="w-full">
                  <GoogleSignInButton
                    mode="auth"
                    text="signup_with"
                    locale={isAr ? 'ar' : 'en'}
                    onSuccess={() => {
                      toast.success(isAr ? 'تم إنشاء حسابك بنجاح!' : 'Account created!');
                      setTimeout(() => { window.location.href = '/'; }, 300);
                    }}
                  />
                </div>
                <div data-testid="apple-register-wrapper" className="w-full">
                  <AppleSignInButton
                    mode="auth"
                    locale={isAr ? 'ar' : 'en'}
                    onSuccess={() => {
                      toast.success(isAr ? 'تم إنشاء حسابك بنجاح!' : 'Account created!');
                      setTimeout(() => { window.location.href = '/'; }, 300);
                    }}
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-wider">
                  <span className={`px-3 ${isDarkMode ? 'bg-[#141414] text-gray-500' : 'bg-white text-gray-500'}`}>
                    {isAr ? 'أو أكمل بالبريد' : 'or continue with email'}
                  </span>
                </div>
              </div>

              <form onSubmit={handleSendCode} className="space-y-5" data-testid="email-form">
                <div>
                  <Label htmlFor="email" className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                    {t('auth.email')} *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`h-12 rounded-xl text-[15px] px-4 transition-all focus-visible:ring-2 focus-visible:ring-[#1E9BD9]/30 focus-visible:border-[#1E9BD9] ${errors.email ? 'border-red-500' : ''} ${isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-[#E2E8F0] placeholder:text-gray-400'}`}
                    data-testid="register-email-input"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.email}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white text-base font-semibold shadow-lg shadow-[#1E9BD9]/25 hover:shadow-[#1E9BD9]/40 active:scale-[0.99] transition-all duration-150"
                  data-testid="send-code-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-5 w-5 animate-spin me-2" /> {t('auth.sending')}</>
                  ) : (
                    <><Mail className="h-5 w-5 me-2" /> {t('auth.sendCode')}</>
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-5" data-testid="verify-form">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#1E9BD9]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-[#1E9BD9]" />
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {t('auth.codeSentTo')}
                </p>
                <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{formData.email}</p>
              </div>

              <div>
                <Label htmlFor="verificationCode" className={`font-body text-sm mb-3 block text-center ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                  {t('auth.enterCode')}
                </Label>
                <div className="flex justify-center" dir="ltr">
                  <InputOTP
                    maxLength={6}
                    value={verificationCode}
                    onChange={(value) => setVerificationCode(value)}
                    data-testid="verification-code-input"
                  >
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot 
                        index={0} 
                        className={`w-12 h-14 text-2xl font-bold rounded-lg border-2 ${
                          isDarkMode 
                            ? 'bg-white/5 border-white/20 text-white' 
                            : 'bg-white border-[#E2E8F0] text-[#0F172A]'
                        } focus:border-[#1E9BD9] focus:ring-2 focus:ring-[#1E9BD9]/20`}
                      />
                      <InputOTPSlot 
                        index={1} 
                        className={`w-12 h-14 text-2xl font-bold rounded-lg border-2 ${
                          isDarkMode 
                            ? 'bg-white/5 border-white/20 text-white' 
                            : 'bg-white border-[#E2E8F0] text-[#0F172A]'
                        } focus:border-[#1E9BD9] focus:ring-2 focus:ring-[#1E9BD9]/20`}
                      />
                      <InputOTPSlot 
                        index={2} 
                        className={`w-12 h-14 text-2xl font-bold rounded-lg border-2 ${
                          isDarkMode 
                            ? 'bg-white/5 border-white/20 text-white' 
                            : 'bg-white border-[#E2E8F0] text-[#0F172A]'
                        } focus:border-[#1E9BD9] focus:ring-2 focus:ring-[#1E9BD9]/20`}
                      />
                      <InputOTPSlot 
                        index={3} 
                        className={`w-12 h-14 text-2xl font-bold rounded-lg border-2 ${
                          isDarkMode 
                            ? 'bg-white/5 border-white/20 text-white' 
                            : 'bg-white border-[#E2E8F0] text-[#0F172A]'
                        } focus:border-[#1E9BD9] focus:ring-2 focus:ring-[#1E9BD9]/20`}
                      />
                      <InputOTPSlot 
                        index={4} 
                        className={`w-12 h-14 text-2xl font-bold rounded-lg border-2 ${
                          isDarkMode 
                            ? 'bg-white/5 border-white/20 text-white' 
                            : 'bg-white border-[#E2E8F0] text-[#0F172A]'
                        } focus:border-[#1E9BD9] focus:ring-2 focus:ring-[#1E9BD9]/20`}
                      />
                      <InputOTPSlot 
                        index={5} 
                        className={`w-12 h-14 text-2xl font-bold rounded-lg border-2 ${
                          isDarkMode 
                            ? 'bg-white/5 border-white/20 text-white' 
                            : 'bg-white border-[#E2E8F0] text-[#0F172A]'
                        } focus:border-[#1E9BD9] focus:ring-2 focus:ring-[#1E9BD9]/20`}
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white text-base font-semibold shadow-lg shadow-[#1E9BD9]/25 hover:shadow-[#1E9BD9]/40 active:scale-[0.99] transition-all duration-150"
                data-testid="verify-code-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin me-2" /> {t('auth.verifying')}</>
                ) : (
                  <><Check className="h-5 w-5 me-2" /> {t('auth.verifyCode')}</>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-center text-[#1E9BD9] hover:underline text-sm"
              >
                {t('auth.changeEmail')}
              </button>
            </form>
          )}

          {/* Step 3: Complete Registration */}
          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
              <div className={`rounded-lg p-3 mb-4 flex items-center gap-2 ${
                isDarkMode 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <Check className="h-5 w-5 text-green-500" />
                <span className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                  {t('auth.emailVerified')}: {formData.email}
                </span>
              </div>

              {/* First & Last Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                    {t('auth.firstName')} *
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    required
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, firstName: value });
                      // Check for Arabic characters
                      if (/[\u0600-\u06FF]/.test(value)) {
                        setErrors(prev => ({ ...prev, firstName: t('auth.englishOnly') }));
                      } else if (value && !/^[A-Za-z]*$/.test(value)) {
                        setErrors(prev => ({ ...prev, firstName: t('auth.englishOnly') }));
                      } else {
                        setErrors(prev => ({ ...prev, firstName: null }));
                      }
                    }}
                    className={`h-12 rounded-xl text-[15px] px-4 transition-all focus-visible:ring-2 focus-visible:ring-[#1E9BD9]/30 focus-visible:border-[#1E9BD9] ${errors.firstName ? 'border-red-500' : ''} ${
                      isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-[#E2E8F0] placeholder:text-gray-400'
                    }`}
                    data-testid="register-firstname-input"
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="lastName" className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                    {t('auth.lastName')} *
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    required
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, lastName: value });
                      // Check for Arabic characters
                      if (/[\u0600-\u06FF]/.test(value)) {
                        setErrors(prev => ({ ...prev, lastName: t('auth.englishOnly') }));
                      } else if (value && !/^[A-Za-z]*$/.test(value)) {
                        setErrors(prev => ({ ...prev, lastName: t('auth.englishOnly') }));
                      } else {
                        setErrors(prev => ({ ...prev, lastName: null }));
                      }
                    }}
                    className={`h-12 rounded-xl text-[15px] px-4 transition-all focus-visible:ring-2 focus-visible:ring-[#1E9BD9]/30 focus-visible:border-[#1E9BD9] ${errors.lastName ? 'border-red-500' : ''} ${
                      isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-[#E2E8F0] placeholder:text-gray-400'
                    }`}
                    data-testid="register-lastname-input"
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs mt-1.5">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <Label htmlFor="password" className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                  {t('auth.password')} *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`h-12 rounded-xl text-[15px] px-4 pe-11 transition-all focus-visible:ring-2 focus-visible:ring-[#1E9BD9]/30 focus-visible:border-[#1E9BD9] ${errors.password ? 'border-red-500' : ''} ${
                      isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-[#E2E8F0] placeholder:text-gray-400'
                    }`}
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute inset-y-0 end-0 flex items-center pe-4 transition-colors ${
                      isDarkMode ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'
                    }`}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                
                {/* Password Rules */}
                <div className={`mt-3 p-3 rounded-xl space-y-1 ${
                  isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-[#F8FAFC] border border-[#E2E8F0]'
                }`}>
                  <p className={`text-xs mb-2 font-medium ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                    {t('auth.passwordRequirements')}:
                  </p>
                  <PasswordRule passed={passwordRules.minLength} text={t('auth.minChars')} isDarkMode={isDarkMode} />
                  <PasswordRule passed={passwordRules.hasUppercase} text={t('auth.uppercase')} isDarkMode={isDarkMode} />
                  <PasswordRule passed={passwordRules.hasLowercase} text={t('auth.lowercase')} isDarkMode={isDarkMode} />
                  <PasswordRule passed={passwordRules.hasNumber} text={t('auth.number')} isDarkMode={isDarkMode} />
                  <PasswordRule passed={passwordRules.hasSymbol} text={t('auth.symbol')} isDarkMode={isDarkMode} />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword" className={`block mb-2 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>
                  {t('auth.confirmPassword')} *
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`h-12 rounded-xl text-[15px] px-4 pe-11 transition-all focus-visible:ring-2 focus-visible:ring-[#1E9BD9]/30 focus-visible:border-[#1E9BD9] ${errors.confirmPassword ? 'border-red-500' : ''} ${
                      isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-[#E2E8F0] placeholder:text-gray-400'
                    }`}
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute inset-y-0 end-0 flex items-center pe-4 transition-colors ${
                      isDarkMode ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'
                    }`}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1.5">{errors.confirmPassword}</p>
                )}
                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <p className="text-green-500 text-xs mt-1.5 flex items-center gap-1">
                    <Check className="h-3 w-3" /> {t('auth.passwordMatch')}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !isPasswordValid}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white text-base font-semibold shadow-lg shadow-[#1E9BD9]/25 hover:shadow-[#1E9BD9]/40 active:scale-[0.99] transition-all duration-150 mt-2"
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin me-2" /> {t('auth.creatingAccount')}</>
                ) : (
                  <><ArrowRight className="h-5 w-5 me-2" /> {t('auth.register')}</>
                )}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className={`font-body ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
              {t('auth.haveAccount')}{' '}
              <Link to="/login" className="text-[#1E9BD9] hover:underline font-medium" data-testid="login-link">
                {t('auth.login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
